import { useNavigate, useSearchParams, useOutletContext } from 'react-router-dom';
import React, { useEffect, useRef, useState } from 'react';

import styles from './deal-taxes.module.css';

import axios from '../../utils/axios';

import { Button, Icon, Response } from '../../components';
import { DealTaxesDetails } from './components/DealTaxesDetails';
import { Sankey } from '../../components/Sankey/components';
import { SelectionPopup } from '../../components/SelectionPopup';
import { ResultTable, ResultTableLeft, ResultTableMiddle, ResultTableRight } from '../../components/ResultTable';
import { generateSankey } from '../../utils';
import {
    SelectedDataFund,
    SelectedItemAsset,
    SelectedItemExtended,
    SelectedItemLegalType,
} from '../../components/SelectionPopup/SelectionPopup';
import camelcaseKeys from 'camelcase-keys';
import TransitionComponent from '../../components/TransitionComponent/TransitionComponents';
import LoaderComponent from '../../components/TransitionComponent/LoaderContent';
import { DealTaxesTips } from './components/DealTaxesTips';
import { Icons } from '../../components/Icon/Icon';
import {
    CorporateLegalTypeIds,
    DealTaxesCheckboxes,
    isDealTaxesCheckboxAvailable,
} from '../../utils/deal-taxes-checkboxes';
import { LinkedButton } from '../../components/Button';
import { DiagramDataResponse, DiagramDataTableShortItem, SankeyData } from '../../types/deal-taxes';
import { dispatchAsidePortalContentChanged } from '../../utils/aside-portal-event';
import { useMenuStore } from '../../store/useMenuStore';
import { type DealTaxesLayoutContextType } from './DealTaxesLayout';
import { useDiagramStore } from '../../store/useDiagramStore';
import { Asset } from '../../types/general';

export interface DealTaxesSelectedData {
    selectedInvestor: SelectedItemExtended | undefined;
    assets: SelectedItemAsset[];
    investors: SelectedItemExtended[];
    investmentFund: SelectedItemLegalType;
    fundOfFunds: undefined | SelectedItemLegalType;

    openEnded: boolean;
    // corporate: boolean;
    booked?: boolean;
    ifBusinessAssets?: boolean;
    ifCommercialPartnership?: boolean;
    fofOpenEnded?: boolean;
    // fofCorporate?: boolean;
    fofIfCommercialPartnership?: boolean;
    ukInvesting?: boolean;
    fofUkInvesting?: boolean;
    // for optimisation
    fundStatus?: string;
}

const DealTaxes = () => {
    // Get context from DealTaxesLayout
    const context = useOutletContext<DealTaxesLayoutContextType>();
    const {
        setOnEdit,
        setSelectedData,
        selectedData,
        checkClicked,
        setCheckClicked,
        toggleRequest,
        setToggleRequest,
        pageReady,
        initialInvestment,
        returnOnAsset,
        distribution,
        showPopup,
        setShowPopup,
        isFoF,
        isOptimisation,
        fund,
        // fofLegalTypesDisabled,
        setFofLegalTypesDisabled,
        fofOpenEndedDisabled,
        setFofOpenEndedDisabled,
        rawData,
        setRawData,
    } = context || {};

    const [loading, setLoading] = useState(false);

    const [error, setError] = useState<boolean>(false);

    const [showDetails, setShowDetails] = useState(false);
    const [sankeyData, setSankeyData] = useState<SankeyData | null>(null);

    const [activeCategory, setActiveCategory] = useState<string | undefined | null>(null);

    const [tableUrl, setTableUrl] = useState('');

    const [legend, setLegend] = useState(null);

    const [legends, setLegends] = useState<any>([]);

    const navigation = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [isFoFExpanded, setIsFoFExpanded] = useState(false);

    const [isMenuOpen] = useMenuStore((state) => [state.isOpen]);
    const [assets] = useDiagramStore((state) => [state.assets]);

    const [graphHeight, setGraphHeight] = useState(700);
    const graphRef = useRef(null);

    useEffect(() => {
        if (toggleRequest) {
            (async () => await getSankeyData(true))();
        }
    }, [toggleRequest]);

    useEffect(() => {
        // if (checkClicked && selectedData.assets.length && selectedData.investors.length && selectedData.currentFund) {
        if (checkClicked && selectedData.assets.length && selectedData.investors.length) {
            (async () => await getSankeyData(false))();
        }
    }, [selectedData.openEnded, selectedData.booked, checkClicked]);

    useEffect(() => {
        if (showPopup) {
            setLoading(true);
        }
    }, [showPopup]);

    // Set handleEdit for this page
    useEffect(() => {
        if (setOnEdit) {
            const handleEdit = () => {
                setShowPopup(true);
                dispatchAsidePortalContentChanged(false);
            };
            setOnEdit(handleEdit);
        }
    }, []);

    const handleFormSubmission = async (formSelectedData: SelectedDataFund) => {
        setShowDetails(false);
        setActiveCategory(null);
        setIsFoFExpanded(false);

        setSelectedData({
            // ...tmp,
            ...selectedData,
            ...formSelectedData,
            selectedInvestor: formSelectedData.investors[0],
            // fundStatus: undefined,
        });
        setShowPopup(false);
        setToggleRequest(true);
    };

    const getOpenEnded = async (currentFund: number, legalTypeId: number) => {
        const { data } = await axios.get('/base/check/taxes-open-ended', {
            params: {
                currentFund,
                legalTypeId,
            },
        });

        return data;
    };

    const getFoFOpenEnded = async (currentFoF: number, legalTypeId: number) => {
        const { data } = await axios.get('/base/check/fof-open-ended', {
            params: {
                currentFoF,
                legalTypeId,
            },
        });

        return data;
    };

    const setTipsLegendId = (tips: DiagramDataTableShortItem[]) => {
        const filteredFullComments = tips.filter((t) => t.textComments !== '');

        const uniqueFullComments = Array.from(new Set(filteredFullComments.map((i) => i.textComments))).map((i) => {
            return filteredFullComments.find((c) => c.textComments === i);
        });

        // TODO: ???
        // @ts-ignore
        for (const [i, uc] of uniqueFullComments.entries()) {
            uc!.legendId = i === 0 ? '*' : i.toString();
        }

        for (const uc of filteredFullComments) {
            if (!uc.legendId) {
                const sameComment = uniqueFullComments.find((t) => t!.textComments === uc.textComments);
                uc.legendId = sameComment!.legendId;
            }
        }
    };

    const setUrlParams = (selectedData: DealTaxesSelectedData) => {
        const tableUrl = new URLSearchParams();

        for (const entry of Object.entries(selectedData)) {
            // @ts-ignore
            if (typeof entry[1] !== 'undefined' && entry[1] !== 'undefined') {
                tableUrl.set(entry[0], JSON.stringify(entry[1]));
            }
        }

        if (isOptimisation) {
            tableUrl.set('optimisation', `${isOptimisation}`);
            tableUrl.set('initialInvestment', `${initialInvestment}`);
            tableUrl.set('returnOnAsset', `${returnOnAsset}`);
            tableUrl.set('distribution', `${distribution}`);
            // tableUrl.set('investmentFund', `${fund.fund}`);
        }

        setTableUrl('?' + tableUrl.toString());

        if (!isOptimisation) {
            setSearchParams(tableUrl.toString());

            return;
        }
    };

    const verifyCheckboxValues = async (changeCheckboxValue: boolean) => {
        const isCorporate = CorporateLegalTypeIds.includes(selectedData.investmentFund.legalTypeId!);
        let isOpenEnded = selectedData.openEnded;

        let ifCommercialPartnership = selectedData.ifCommercialPartnership;

        let fofOpenEnded = selectedData.fofOpenEnded;
        let fofIfCommercialPartnership = selectedData.fofIfCommercialPartnership;
        const fofCorporate = CorporateLegalTypeIds.includes(selectedData.fundOfFunds?.legalTypeId!);

        let ifBusinessAssets = selectedData.ifBusinessAssets;
        let booked = selectedData.booked;

        let ukInvesting = selectedData.ukInvesting;
        let fofUkInvesting = selectedData.fofUkInvesting;

        if (!isDealTaxesCheckboxAvailable(DealTaxesCheckboxes.ifBusinessAssets, selectedData)) {
            ifBusinessAssets = undefined;
            if (typeof booked === 'undefined') {
                booked = true;
            }
        } else {
            booked = undefined;
            if (typeof ifBusinessAssets === 'undefined' || [14, 15].includes(selectedData.selectedInvestor?.id!)) {
                ifBusinessAssets = true;
            }
        }

        if (!isDealTaxesCheckboxAvailable(DealTaxesCheckboxes.ukInvesting, selectedData)) {
            ukInvesting = undefined;
        } else {
            if (typeof ukInvesting === 'undefined') {
                ukInvesting = true;
            }
        }

        if (!!selectedData.fundOfFunds) {
            if (changeCheckboxValue || typeof fofOpenEndedDisabled === 'undefined') {
                const fofCheck = await getFoFOpenEnded(
                    selectedData.fundOfFunds.id!,
                    selectedData.fundOfFunds.legalTypeId!,
                );

                const fofLTDisabled =
                    fofCheck.filter(
                        (value: any, index: number, self: any) =>
                            self.map((v: any) => v.fofLegalType).indexOf(value.fofLegalType) === index,
                    ).length === 1;

                setFofLegalTypesDisabled(fofLTDisabled);

                const fofOEDisabled =
                    fofCheck.filter(
                        (value: any, index: number, self: any) =>
                            self.map((v: any) => v.fofIfOpenEnded).indexOf(value.fofIfOpenEnded) === index,
                    ).length === 1;

                setFofOpenEndedDisabled(fofOEDisabled);

                if (changeCheckboxValue) {
                    // fofCorporate = fofCheck[1]?.legalType.trim() === 'corporate';
                    fofOpenEnded = fofCheck[0]?.fofIfOpenEnded;
                }
            }
        } else {
            fofOpenEnded = undefined;
            fofIfCommercialPartnership = undefined;
            // fofCorporate = undefined;
            fofUkInvesting = undefined;
        }

        if (changeCheckboxValue) {
            const openEnded = await getOpenEnded(
                selectedData.investmentFund.id!,
                selectedData.investmentFund.legalTypeId!,
            );

            // isCorporate = openEnded[0]?.legalType.trim() === 'corporate';
            isOpenEnded = openEnded[0]?.ifOpenEnded;

            if (
                isDealTaxesCheckboxAvailable(DealTaxesCheckboxes.ukInvesting, selectedData) &&
                (typeof ukInvesting !== 'undefined' || (isCorporate && isOpenEnded))
            ) {
                ukInvesting = true;
            }

            if (
                isDealTaxesCheckboxAvailable(DealTaxesCheckboxes.fofUkInvesting, selectedData) &&
                (typeof fofUkInvesting !== 'undefined' || (fofCorporate && fofOpenEnded))
            ) {
                fofUkInvesting = true;
            }
        }

        if (isDealTaxesCheckboxAvailable(DealTaxesCheckboxes.ifCommercialPartnership, selectedData)) {
            if (typeof ifCommercialPartnership === 'undefined' || isCorporate) {
                ifCommercialPartnership = true;
            }
        } else {
            ifCommercialPartnership = undefined;
        }

        if (isDealTaxesCheckboxAvailable(DealTaxesCheckboxes.fofIfCommercialPartnership, selectedData)) {
            if (typeof fofIfCommercialPartnership === 'undefined' || fofCorporate) {
                fofIfCommercialPartnership = true;
            }
        } else {
            fofIfCommercialPartnership = undefined;
        }

        return {
            booked,
            ifBusinessAssets,
            // corporate: isCorporate,
            openEnded: isOpenEnded || false,
            ifCommercialPartnership,
            fofOpenEnded,
            fofIfCommercialPartnership,
            // fofCorporate,
            ukInvesting,
            fofUkInvesting,
        };
    };

    const getSankeyData = async (changeCheckboxValue: boolean) => {
        // if (!selectedData.assets.length) return;

        setToggleRequest(false);
        setCheckClicked(false);

        const checkboxes = await verifyCheckboxValues(changeCheckboxValue);

        const tmp = {
            ...selectedData,
            ...checkboxes,
        };

        setSelectedData(tmp);
        dispatchAsidePortalContentChanged(true);

        try {
            const { data } = await axios.get<DiagramDataResponse>('/deal-taxes/diagram', {
                params: {
                    ...tmp,
                },
            });

            const sankey = generateSankey(camelcaseKeys(data.result));

            setTipsLegendId(data.result.popUpTable.flat());
            setRawData(data.result);
            setSankeyData(sankey);
            setLegends(sankey.tips.map((tip) => tip.data).flat(1));
        } catch (e) {
            setTipsLegendId([]);
            setRawData(null);
            setSankeyData(null);
            setLegends([]);
        } finally {
            setUrlParams(tmp);

            // setTimeout(() => {
            setLoading(false);
            // }, 100);
        }
    };

    const savePreset = async (e: any) => {
        e.preventDefault();

        try {
            await axios.post('/presets/deal-taxes', selectedData);

            // redirect
            navigation('/dashboard?save=1');
        } catch (e) {
            setError(true);
        }
    };

    const onDataChange = (category: string | null) => {
        setActiveCategory(category);
        if (category) {
            // @ts-ignore
            setGraphHeight(graphRef?.current?.clientHeight || graphHeight);
            setShowDetails(true);
        } else {
            setShowDetails(false);
        }
    };

    const handleDetailsClose = () => {
        setShowDetails(false);
        setActiveCategory(null);
    };

    const onLegendChange = (legendId: any) => {
        setLegend(legendId);
    };

    const validateSelection = async (data: SelectedDataFund) => {
        if (
            !data.investors.filter((i) => i.id).length ||
            !data.assets.filter((i) => i.id).length ||
            !data.investmentFund?.id ||
            (data.fundOfFunds && !data.fundOfFunds.id)
        )
            return true;

        await axios.get(`/base${data.fundOfFunds ? '/fof' : ''}/check/selection`, {
            params: {
                currentInvestors: data.investors
                    .filter((i) => i.id)
                    .map((i) => i.id)
                    .flat(),
                assetClasses: data.assets
                    .filter((i) => i.id)
                    .map((a) => a.name)
                    .flat(),
                currentAssetTaxIds: data.assets
                    .filter((i) => i.id)
                    .map((a) => a.currentAssetTaxId)
                    .flat(),
                currentFund: data.investmentFund?.id,
                currentFoF: data.fundOfFunds?.id,
            },
        });
        return true;
    };

    return (
        <>
            <TransitionComponent inProp={showPopup} isAbsolute={true} duration={100}>
                <SelectionPopup isMulti={true} onSubmit={handleFormSubmission} validateSelection={validateSelection} />
            </TransitionComponent>

            <LoaderComponent inProp={pageReady} hideLoader={true}>
                <>
                    <ResultTable>
                        <ResultTableLeft />

                        <ResultTableMiddle>
                            <LoaderComponent inProp={!loading}>
                                <div className={styles.graph}>
                                    <div className={`${isFoFExpanded ? 'fof-active' : ''} ${styles.graphContainer}`}>
                                        {sankeyData && sankeyData.links && (
                                            <>
                                                <Sankey
                                                    graphRef={graphRef}
                                                    sankey={sankeyData}
                                                    activeCategory={activeCategory}
                                                    onDataChange={onDataChange}
                                                    onLegendChange={onLegendChange}
                                                />

                                                {isFoF && (
                                                    <div className={styles.expandBtn}>
                                                        <Button
                                                            onClick={() => {
                                                                setIsFoFExpanded(!isFoFExpanded);
                                                            }}
                                                            outlined={true}
                                                            before={
                                                                !isFoFExpanded && (
                                                                    <span style={{ width: '10px' }}>
                                                                        <Icon icon={Icons.zoom} />
                                                                    </span>
                                                                )
                                                            }
                                                            after={
                                                                isFoFExpanded && (
                                                                    <span style={{ width: '10px' }}>
                                                                        <Icon icon={Icons.close} />
                                                                    </span>
                                                                )
                                                            }
                                                            size={'xs'}
                                                        >
                                                            {isFoFExpanded ? 'Expanded' : 'Expand'}
                                                        </Button>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </LoaderComponent>
                        </ResultTableMiddle>

                        {/*{rawData && (*/}
                        <ResultTableRight
                            showDetails={showDetails}
                            details={
                                <DealTaxesDetails
                                    activeCategory={activeCategory}
                                    tips={rawData?.popUpTable?.flat() || []}
                                    onClose={handleDetailsClose}
                                    legendChange={onLegendChange}
                                    height={graphHeight}
                                    isMultiAsset={selectedData.assets.length > 1}
                                    asset={
                                        selectedData.assets?.[0]
                                            ? assets[selectedData.assets?.[0]?.countryCode!]?.find(
                                                  (a: Asset) => a.assetId == selectedData.assets[0].id,
                                              )
                                            : undefined
                                    }
                                />
                            }
                            isFoF={isFoF}
                            isOptimisation={isOptimisation}
                            // tips={<DealTaxesTips />}
                        />
                        {/*)}*/}
                    </ResultTable>

                    <ResultTable isMain={false}>
                        <ResultTableLeft
                            comments={legend ? legends.find((l: any) => l.legendId === legend) : null}
                            isMenuOpen={isMenuOpen}
                            showComments={!!legend}
                            isOptimisation={isOptimisation}
                            fundData={fund}
                        ></ResultTableLeft>
                        <ResultTableMiddle>
                            {/*{!isFoF && !isOptimisation && (*/}
                            <div className={styles.actions}>
                                {/*{isFoF && (*/}
                                {/*    <p className={styles.tip}>Click on the visualization to explore the details</p>*/}
                                {/*)}*/}

                                <LinkedButton
                                    to={{
                                        pathname: '/deal-taxes/expanded',
                                        search: tableUrl,
                                    }}
                                    style={{
                                        minWidth: 170,
                                    }}
                                >
                                    Learn more
                                </LinkedButton>

                                <Button
                                    onClick={savePreset}
                                    style={{
                                        minWidth: 170,
                                    }}
                                >
                                    Save review
                                </Button>

                                {isOptimisation && (
                                    <LinkedButton
                                        to={{
                                            pathname: '/tax-optimisation',
                                            search: searchParams.toString(),
                                        }}
                                        after={<Icon icon={Icons.return} />}
                                        style={{
                                            minWidth: 170,
                                        }}
                                    >
                                        Back to options
                                    </LinkedButton>
                                )}
                            </div>
                        </ResultTableMiddle>
                        <ResultTableRight showDetails={showDetails} tips={<DealTaxesTips />}></ResultTableRight>
                    </ResultTable>
                </>
            </LoaderComponent>

            {error && (
                <Response type={'error'} aligned={false} setResponse={setError}>
                    An error occurred.
                </Response>
            )}
        </>
    );
};

export default DealTaxes;
